import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { MetricRationale } from '../../types/stockAnalytics';

const fmtInt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR'));
const fmtDec = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

/** Painel "Como foi calculado" — renderiza o RationaleJson persistido pelo batch. */
export const RationalePanel: React.FC<{ rationaleJson: string | null }> = ({ rationaleJson }) => {
  const rationale = useMemo<MetricRationale | null>(() => {
    if (!rationaleJson) return null;
    try {
      return JSON.parse(rationaleJson) as MetricRationale;
    } catch {
      return null;
    }
  }, [rationaleJson]);

  if (!rationale) return <p className="text-sm text-slate-400">Racional de cálculo indisponível.</p>;

  const section = (title: string, formula: string, lines: Array<[string, string]>) => (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <p className="text-xs font-semibold text-slate-700">{title}</p>
      <p className="text-[11px] font-mono text-slate-500 mt-1 whitespace-pre-wrap">{formula}</p>
      <dl className="mt-2 space-y-0.5">
        {lines.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-xs">
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-slate-800 font-medium text-right">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {section('Demanda', rationale.demanda.formula, [
          ['Janela / meses com dados', `${rationale.demanda.janela_meses}m / ${rationale.demanda.meses_com_dados}m`],
          ['Demanda total na janela', fmtInt(rationale.demanda.demanda_total_janela)],
          ['Média diária', fmtDec(rationale.demanda.media_diaria, 4)],
          ['Desvio padrão diário', fmtDec(rationale.demanda.desvio_padrao_diario, 4)],
          ['Coef. de variação', fmtDec(rationale.demanda.coeficiente_variacao, 3)],
        ])}
        {section('Sazonalidade', rationale.sazonalidade.formula, [
          ['Aplicada', rationale.sazonalidade.aplicada ? 'Sim' : `Não — ${rationale.sazonalidade.motivo ?? ''}`],
          ['Fator no horizonte de compra', fmtDec(rationale.sazonalidade.fator_horizonte, 3)],
        ])}
        {section('Lead time', rationale.lead_time.formula, [
          ['Fonte', rationale.lead_time.fonte],
          ['Média ± desvio (dias)', `${fmtDec(rationale.lead_time.media_dias)} ± ${fmtDec(rationale.lead_time.desvio_dias)}`],
          ['Amostras', fmtInt(rationale.lead_time.amostras)],
        ])}
        {section('Estoque de segurança', rationale.estoque_seguranca.formula, [
          ['Nível de serviço', `${fmtDec(rationale.estoque_seguranca.nivel_servico_percent)}%`],
          ['Fator Z', fmtDec(rationale.estoque_seguranca.fator_z, 4)],
          ['Estoque de segurança', fmtDec(rationale.estoque_seguranca.estoque_seguranca, 2)],
          ['Ponto de pedido', fmtDec(rationale.estoque_seguranca.ponto_pedido, 2)],
        ])}
        {section('Sugestões', `${rationale.sugestoes.formula_min}\n${rationale.sugestoes.formula_max}\n${rationale.sugestoes.formula_compra}`, [
          ['Ciclo de revisão', `${rationale.sugestoes.ciclo_revisao_dias} dias`],
          ['Demanda diária prevista', fmtDec(rationale.sugestoes.demanda_diaria_prevista, 4)],
          ['Mín / Máx sugeridos', `${fmtInt(rationale.sugestoes.min_sugerido)} / ${fmtInt(rationale.sugestoes.max_sugerido)}`],
          ['Estoque disponível + em pedido', `${fmtInt(rationale.sugestoes.estoque_disponivel)} + ${fmtDec(rationale.sugestoes.qtd_em_pedido)}`],
          ['Compra sugerida', fmtInt(rationale.sugestoes.compra_sugerida)],
        ])}
        {section('Qualidade dos dados', 'Confiança pelo histórico e pela cobertura do snapshot diário', [
          ['Meses de histórico', fmtInt(rationale.qualidade_dados.meses_historico)],
          ['Cobertura do snapshot (12m)', `${rationale.qualidade_dados.cobertura_snapshot_percent}%`],
          ['Confiança', rationale.qualidade_dados.confianca],
        ])}
      </div>
      {rationale.observacoes && rationale.observacoes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
          {rationale.observacoes.map((obs, i) => (
            <p key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {obs}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default RationalePanel;
